/** Analytics → Statistics.
 *
 *  Starts with two charts — hot_offers ingest volume by hour (our own
 *  dedup/classification pipeline) and a standalone cross-check straight
 *  from the ssparser scraper's raw ss.ge feed — plus the full numeric
 *  breakdown of the first as a table underneath. Future charts land as
 *  additional ``<div className="card p-4">`` blocks below these, each
 *  independently fetched like the Competition dashboard's widgets.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, api } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import {
  RangeChips,
  buildRangeQuery,
  todayLocalISO,
  addDaysLocal,
  type CustomRange,
  type Range,
} from "@/components/RangeChips";
import { Skeleton } from "@/components/Skeleton";

/** Keep in sync with ``app/schemas/stats.py:HotOfferHourlyPoint``. */
interface HotOfferHourlyPoint {
  hour: string;
  agent: number;
  owner: number;
  owner_ss: number;
  owner_myhome: number;
  developer: number;
  unclassified: number;
  ss: number;
  myhome: number;
  total: number;
}

interface HotOfferHourlyOut {
  points: HotOfferHourlyPoint[];
}

const HOT_OFFER_SERIES: SeriesDef<HotOfferHourlyPoint>[] = [
  { key: "agent", label: "agent", color: "rgb(99 102 241 / 0.8)" },
  { key: "owner", label: "owner", color: "rgb(16 185 129 / 0.8)" },
  { key: "developer", label: "developer", color: "rgb(245 158 11 / 0.8)" },
];

export function AnalyticsStats() {
  const [range, setRange] = useState<Range>("7d");
  const [customRange, setCustomRange] = useState<CustomRange>(() => {
    const today = todayLocalISO();
    return { from: addDaysLocal(today, -6), to: today };
  });
  const customKey =
    range === "custom" ? `${customRange.from}|${customRange.to}` : "";

  const [data, setData] = useState<HotOfferHourlyOut | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Shared with the chart so hovering a bar highlights its table row and
  // vice versa — the two views are two windows onto the same hour.
  const [hoveredHour, setHoveredHour] = useState<string | null>(null);

  const load = useCallback(
    async (signal: AbortSignal) => {
      try {
        const qs = buildRangeQuery(range, customRange);
        const r = await api<HotOfferHourlyOut>(
          `/api/stats/hot-offers-hourly?${qs.toString()}`,
          { signal },
        );
        if (signal.aborted) return;
        setData(r);
        setErr(null);
      } catch (e) {
        if (signal.aborted) return;
        if (e instanceof ApiError) setErr(e.message);
        else if (e instanceof Error) setErr(e.message);
        else setErr("Request failed");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [range, customKey],
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  useEffect(() => {
    setHoveredHour(null);
  }, [range, customKey]);

  const points = data?.points ?? [];

  return (
    <div className="w-full space-y-4">
      <PageHeader
        title="Statistics"
        subtitle="Hot-offers ingest volume by hour. More charts land here over time."
        actions={
          <RangeChips
            value={range}
            onChange={setRange}
            customRange={customRange}
            onCustomChange={setCustomRange}
          />
        }
      />

      <div className="card p-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">
          Uploads by hour
        </div>
        {err && <div className="text-rose-300 text-xs mb-2">{err}</div>}
        {!data ? (
          <Skeleton className="h-48" />
        ) : points.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">
            No data in this window.
          </div>
        ) : (
          <StackedHourlyChart
            points={points}
            series={HOT_OFFER_SERIES}
            hoveredHour={hoveredHour}
            onHover={setHoveredHour}
            footer={(p) => `SS ${p.ss} · MyHome ${p.myhome}`}
          />
        )}
      </div>

      <div className="card p-4">
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">
          Detail
        </div>
        {!data ? (
          <Skeleton className="h-48" />
        ) : points.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">
            No data in this window.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs whitespace-nowrap">
              <thead>
                <tr className="text-slate-500 text-left border-b border-white/10">
                  <th className="py-1.5 pr-4">Hour</th>
                  <th className="py-1.5 pr-4 text-right">Agent</th>
                  <th className="py-1.5 pr-4 text-right">Owner</th>
                  <th className="py-1.5 pr-4 text-right">Owner (SS)</th>
                  <th className="py-1.5 pr-4 text-right">Owner (MyHome)</th>
                  <th className="py-1.5 pr-4 text-right">Developer</th>
                  <th className="py-1.5 pr-4 text-right">Unclassified</th>
                  <th className="py-1.5 pr-4 text-right">SS</th>
                  <th className="py-1.5 pr-4 text-right">MyHome</th>
                  <th className="py-1.5 pr-4 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {[...points].reverse().map((p) => (
                  <tr
                    key={p.hour}
                    onMouseEnter={() => setHoveredHour(p.hour)}
                    onMouseLeave={() => setHoveredHour(null)}
                    className={`border-b border-white/5 cursor-default transition-colors ${
                      p.hour === hoveredHour
                        ? "bg-accent-500/10"
                        : "hover:bg-white/[0.03]"
                    }`}
                  >
                    <td className="py-1 pr-4 text-slate-300">{p.hour}</td>
                    <td className="py-1 pr-4 text-right">{p.agent}</td>
                    <td className="py-1 pr-4 text-right">{p.owner}</td>
                    <td className="py-1 pr-4 text-right text-slate-500">
                      {p.owner_ss}
                    </td>
                    <td className="py-1 pr-4 text-right text-slate-500">
                      {p.owner_myhome}
                    </td>
                    <td className="py-1 pr-4 text-right">{p.developer}</td>
                    <td className="py-1 pr-4 text-right text-slate-500">
                      {p.unclassified}
                    </td>
                    <td className="py-1 pr-4 text-right">{p.ss}</td>
                    <td className="py-1 pr-4 text-right">{p.myhome}</td>
                    <td className="py-1 pr-4 text-right font-semibold text-slate-200">
                      {p.total}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Chart ──────────────────────────────────────────────────────────────

interface SeriesDef<T> {
  key: keyof T & string;
  label: string;
  color: string;
}

/** Generic stacked hourly bar chart shared by every Statistics widget —
 *  hover tooltip anchored above the bar, click-to-toggle legend, and a
 *  hover callback so the caller can sync a table row / a sibling chart. */
function StackedHourlyChart<T extends { hour: string; total: number }>({
  points,
  series,
  hoveredHour,
  onHover,
  footer,
}: {
  points: T[];
  series: SeriesDef<T>[];
  hoveredHour: string | null;
  onHover: (hour: string | null) => void;
  footer?: (p: T) => string;
}) {
  // Legend click toggles a series out of the stack — lets you isolate
  // e.g. just "owner" volume without the agent bars dwarfing it.
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const toggleSeries = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const W = 900;
  const H = 300;
  // Left gutter holds the y-axis value labels; top gutter holds the
  // legend row above the plot so bars get the full remaining height.
  const pad = { l: 46, r: 14, t: 44, b: 28 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const n = points.length;
  const colW = innerW / Math.max(1, n);
  const visibleSeries = series.filter((s) => !hidden.has(s.key));
  const value = (p: T, key: string) => Number(p[key as keyof T]) || 0;
  const maxTotal = Math.max(
    1,
    ...points.map((p) =>
      visibleSeries.reduce((sum, s) => sum + value(p, s.key), 0),
    ),
  );

  // Sparse x-axis labels — at most ~8 across the width, regardless of
  // how many hourly bars are in the window (up to 720 for 30 days).
  const labelStride = Math.max(1, Math.ceil(n / 8));
  const legendCellW = Math.min(120, innerW / Math.max(1, series.length));

  const hoveredIndex = points.findIndex((p) => p.hour === hoveredHour);
  const hovered = hoveredIndex >= 0 ? points[hoveredIndex] : null;
  // Top of the hovered column's stack, in viewBox units — the tooltip
  // anchors just above it instead of floating at a fixed height.
  const hoveredStackTop = useMemo(() => {
    if (!hovered) return pad.t;
    const stacked = visibleSeries.reduce(
      (sum, s) => sum + value(hovered, s.key),
      0,
    );
    return pad.t + innerH - (stacked / maxTotal) * innerH;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hovered, visibleSeries, maxTotal, innerH]);

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-72 block"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Stacked bar chart of ${series
          .map((s) => s.label)
          .join(", ")} across ${n} hourly buckets`}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const y = pad.t + innerH - f * innerH;
          return (
            <g key={f}>
              <line
                x1={pad.l}
                x2={W - pad.r}
                y1={y}
                y2={y}
                stroke="rgb(148 163 184 / 0.18)"
                strokeWidth="1"
              />
              {/* Y-axis value label so bar magnitudes are readable at a
                  glance instead of only on hover. */}
              <text
                x={pad.l - 8}
                y={y + 4}
                fontSize="12"
                textAnchor="end"
                fill="rgb(148 163 184)"
              >
                {Math.round(f * maxTotal)}
              </text>
            </g>
          );
        })}
        {points.map((p, i) => {
          const colX = pad.l + i * colW;
          const x = colX + colW * 0.12;
          const w = Math.max(0.5, colW * 0.76);
          const isHovered = p.hour === hoveredHour;
          let yTop = pad.t + innerH;
          return (
            <g key={p.hour}>
              {isHovered && (
                <rect
                  x={colX}
                  y={pad.t}
                  width={colW}
                  height={innerH}
                  fill="rgb(255 255 255 / 0.05)"
                />
              )}
              {visibleSeries.map((s) => {
                const v = value(p, s.key);
                const h = (v / maxTotal) * innerH;
                yTop -= h;
                if (h <= 0) return null;
                return (
                  <rect
                    key={s.key}
                    x={x}
                    y={yTop}
                    width={w}
                    height={h}
                    fill={s.color}
                    stroke={isHovered ? "rgb(255 255 255 / 0.4)" : "none"}
                    strokeWidth={isHovered ? 0.75 : 0}
                  />
                );
              })}
              {/* Full-height hit target so hovering thin/zero bars still
                  works, and hover state stays in sync with the table. */}
              <rect
                x={colX}
                y={pad.t}
                width={colW}
                height={innerH}
                fill="transparent"
                onMouseEnter={() => onHover(p.hour)}
                onMouseLeave={() => onHover(null)}
                style={{ cursor: "pointer" }}
              />
              {i % labelStride === 0 && (
                <text
                  x={x + w / 2}
                  y={H - 8}
                  fontSize="11"
                  textAnchor="middle"
                  fill="rgb(148 163 184)"
                >
                  {p.hour.slice(5, 16)}
                </text>
              )}
            </g>
          );
        })}
        <g
          transform={`translate(${pad.l}, 18)`}
          fontSize="12"
          fill="rgb(203 213 225)"
        >
          {series.map((s, i) => {
            const isHidden = hidden.has(s.key);
            return (
              <g
                key={s.key}
                transform={`translate(${i * legendCellW}, 0)`}
                onClick={() => toggleSeries(s.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleSeries(s.key);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-pressed={!isHidden}
                aria-label={`${s.label} series${isHidden ? " (hidden)" : ""}`}
                style={{ cursor: "pointer", outline: "none" }}
                opacity={isHidden ? 0.4 : 1}
              >
                <rect
                  x="-6"
                  y="-9"
                  width={legendCellW}
                  height="18"
                  rx="4"
                  fill="transparent"
                />
                <circle cx="5" cy="0" r="5" fill={s.color} />
                <text
                  x="15"
                  y="4"
                  textDecoration={isHidden ? "line-through" : "none"}
                >
                  {s.label}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
      {hovered && (
        <div
          className="pointer-events-none absolute z-10 min-w-[140px] rounded-md border border-white/10 bg-ink-900/95 px-2.5 py-1.5 text-[11px] text-slate-300 shadow-xl transition-opacity"
          style={{
            // Anchor to the bar centre in viewBox x-units (the plot is
            // inset by the left y-axis gutter), expressed as a % of width.
            left: `${((pad.l + (hoveredIndex + 0.5) * colW) / W) * 100}%`,
            top: `${(hoveredStackTop / H) * 100}%`,
            transform: "translate(-50%, calc(-100% - 8px))",
          }}
        >
          <div className="font-semibold text-slate-100 mb-1">
            {hovered.hour}
          </div>
          {series.map((s) => (
            <div key={s.key} className="flex items-center gap-1.5">
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ background: s.color }}
              />
              <span className="capitalize">{s.label}</span>
              <span className="ml-auto tabular-nums text-slate-200">
                {value(hovered, s.key)}
              </span>
            </div>
          ))}
          {footer && (
            <div className="mt-1 pt-1 border-t border-white/10 flex justify-between text-slate-500">
              <span>{footer(hovered)}</span>
            </div>
          )}
          <div className="flex justify-between font-semibold text-slate-100">
            <span>Total</span>
            <span className="tabular-nums">{hovered.total}</span>
          </div>
        </div>
      )}
    </div>
  );
}
